import { useEffect } from "react";
import { X } from "@phosphor-icons/react";
import { useSheetDrag } from "@/components/motion/useSheetDrag";

// Bottom sheet on mobile, centered dialog on desktop. Footer is pinned and safe-area aware.
export default function ResponsiveSheet({ open = true, onClose, title, subtitle, headerExtra, footer, children, width = 640, testId, zIndex = 50 }) {
  const { panelRef, backdropRef, requestClose } = useSheetDrag({ onClose, enabled: open });
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, requestClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-6" style={{ zIndex }}>
      <div ref={backdropRef} className="rw-sheet-backdrop absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={requestClose} data-testid={testId ? `${testId}-backdrop` : undefined} />
      <div
        ref={panelRef}
        className="rw-sheet-panel relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl sm:max-h-[88dvh] sm:rounded-xl"
        style={{ maxWidth: `min(${width}px, 100%)` }}
        role="dialog"
        aria-modal="true"
        data-testid={testId}
      >
        <div className="flex shrink-0 justify-center pb-1 pt-2 sm:hidden" data-testid={testId ? `${testId}-handle` : undefined}><div className="h-1 w-10 rounded-full bg-muted-foreground/30" /></div>
        {(title || headerExtra) && (
          <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3 sm:px-5">
            <div className="min-w-0 flex-1">
              {title && <h3 className="truncate text-base font-bold text-foreground">{title}</h3>}
              {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
            </div>
            {headerExtra}
            <button onClick={requestClose} className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Zatvori" data-testid={testId ? `${testId}-close` : undefined}>
              <X size={18} />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-sheet-scroll>{children}</div>
        {footer && (
          <div className="flex shrink-0 items-center gap-2 border-t border-border bg-card px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
